import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CUSTOMER_ORDERS_PREVIEW_MAX,
  useCustomerOrderSummary,
  useCustomerUpcomingOrders,
} from '../../../../api/orderApi';
import type { OrderFilters, OrderItem } from '../../../../types';
import { usePaywallGate } from '../../../../utils/paywall';
import { pendingBalanceOf } from '../../../../utils/paymentAmount';
import { calendarDaysUntil } from '../../../../utils/dateUtils';
import { exportOrdersPdf, exportOrdersXlsx, printOrdersDocument } from '../../../orders/utils/exportOrders';
import ShareCustomerModal from './ShareCustomerModal';

interface Props {
  customerId: string;
  customerName?: string;
}

type DirectionTab = 'expense' | 'income';

/** 该方向的分类名：/orders 的 orderCategory 收的是**当前登录用户视角**的名称，服务端做视角翻转 */
const CATEGORY_BY_TAB: Record<DirectionTab, string> = {
  expense: '支出订单',
  income: '收入订单',
};

function fmtDate(d: string | null): string {
  if (!d) return '-';
  return `${d.slice(0, 4)}/${d.slice(5, 7)}/${d.slice(8, 10)}`;
}

function fmtMoney(n: number): string {
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`;
}

/**
 * 「还剩几天」那一格。
 * 列表只放未执行的单，日子远近就是这张表唯一需要排序依据之外的信息，
 * 已经过去的单继续显示「N 天后」会读成假话，所以负数换成逾期。
 */
function dueLabel(days: number | null): { text: string; cls: string } | null {
  if (days === null) return null;
  if (days < 0) return { text: `逾期 ${-days} 天`, cls: 'text-rose-500' };
  if (days === 0) return { text: '今天', cls: 'text-rose-500 font-semibold' };
  if (days === 1) return { text: '明天', cls: 'text-orange-500 font-semibold' };
  return { text: `${days} 天后`, cls: 'text-gray-400' };
}

export default function CustomerOrders({ customerId, customerName }: Props) {
  const navigate = useNavigate();
  const gate = usePaywallGate();
  const [activeTab, setActiveTab] = useState<DirectionTab>('expense');
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // 核算读数由服务端按**该客户全部订单**求和（GET /orders/customer-summary）。
  // 此前是在这里拉 200 条再本地 reduce：客户一旦过 200 单，第 201 张之后的欠款就不参与求和，
  // 页面上是一个「看着正常其实偏小」的欠款数，比报错难查得多。
  const summary = useCustomerOrderSummary(customerId);
  // 列表只放各方向最近的未执行单，再多就指向订单列表页——上百条单平铺在档案页里既读不完也没意义。
  // 只在本 tab 打开时才取（切回来命中缓存），两个方向各 10 条。
  const expenseList = useCustomerUpcomingOrders(customerId, CATEGORY_BY_TAB.expense, activeTab === 'expense');
  const incomeList = useCustomerUpcomingOrders(customerId, CATEGORY_BY_TAB.income, activeTab === 'income');

  const totals = summary.data;
  const current = activeTab === 'expense' ? expenseList : incomeList;
  const orders = current.data ?? [];
  const directionCount = activeTab === 'expense' ? totals?.expenseOrders : totals?.incomeOrders;
  const hasAnyOrders = (totals?.validOrders ?? 0) > 0 || orders.length > 0;

  const goAllOrders = () => {
    const name = customerName ? `&customerName=${encodeURIComponent(customerName)}` : '';
    navigate(`/orders?customer=${customerId}${name}`);
  };

  const handleExport = async (format: 'xlsx' | 'pdf' | 'print', onlyUnpaid = false) => {
    setExportMenuOpen(false);
    // 与订单列表页同一套点击时判定：菜单先收起来，再等服务端会员状态落地才判，
    // 状态没回来就判会恒为假，尊享用户也会被弹去开通。
    const gateOf = { xlsx: ['exportCsv', '导出 Excel'], pdf: ['exportPrint', '导出 PDF'], print: ['exportPrint', '打印'] } as const;
    const [featureKey, label] = gateOf[format];
    if (!(await gate(featureKey, label))) return;
    setExporting(true);
    try {
      // 空筛选 + options.customerId：这一格的日期/状态由「导出这一个客户」决定，不跟着别处走。
      const emptyFilters: OrderFilters = {
        keyword: '', orderStatus: '', paymentStatus: '',
        dateFrom: '', dateTo: '', serviceType: '', customerId: '', upcoming: false,
      };
      const opts = {
        customerId,
        customerName,
        // 「未付款」是服务端已有的那一档，不另造口径：它自带 `order_amount > 0` 闸门，
        // 0 元历史单因此不会被当成欠款单混进对账表（写入侧已禁，见后端 order-amount-guard）。
        // 文件名那格是给下载目录用的：两份台账同名就只能靠修改时间猜哪份是欠款单。
        ...(onlyUnpaid ? { paymentStatus: '未付款', filenameScope: '未付款' } : {}),
      };
      if (format === 'pdf') {
        await exportOrdersPdf(emptyFilters, opts);
      } else if (format === 'print') {
        await printOrdersDocument(emptyFilters, opts);
      } else {
        await exportOrdersXlsx(emptyFilters, opts);
      }
    } catch (e) {
      console.error('导出失败:', e);
      // 导出已在服务端做会员鉴权，静默失败会让人以为按钮坏了。
      alert(e instanceof Error && e.message ? e.message : '导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  };

  /**
   * 分享核对单：会员墙只挡「点这一下」，读数不挂键——所以判定与导出走同一个 gate，
   * 而不是在渲染时把按钮藏掉（掉级用户的已发链接照样能撤）。
   */
  const handleShare = async () => {
    if (!(await gate('shareOrder', '客户订单分享'))) return;
    setShareOpen(true);
  };

  if (summary.isLoading) {
    return (
      <div className="space-y-2.5 mt-4">
        <div className="h-16 bg-gray-50 rounded-xl animate-pulse" />
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 bg-gray-50 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  // 读数取不到就是「这一页的钱不算数」，不能空着四格让人以为欠款是 0。
  if (summary.isError) {
    return (
      <div className="mt-4 flex flex-col items-center gap-2 py-6 text-center">
        <p className="text-xs text-gray-500">客户核算读数读取失败</p>
        <button
          onClick={() => summary.refetch()}
          className="px-3 py-1.5 text-xs rounded-lg border border-teal-200 text-teal-600 hover:bg-teal-50 cursor-pointer transition-colors"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* 核算读数：四格全部来自服务端，逐单夹零后求和 */}
      <div className={`grid gap-3 mb-5 ${totals && totals.nettingOffset > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-3 text-center border border-blue-100">
          <p className="text-xl font-bold text-blue-700">{totals?.validOrders ?? 0}</p>
          <p className="text-[11px] text-blue-500 mt-0.5">有效订单</p>
        </div>
        <div className="bg-gradient-to-br from-rose-50 to-rose-100/50 rounded-xl p-3 text-center border border-rose-100">
          <p className="text-xl font-bold text-rose-600">{fmtMoney(totals?.expensePending ?? 0)}</p>
          <p className="text-[11px] text-rose-500 mt-0.5">待付款</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-3 text-center border border-amber-100">
          <p className="text-xl font-bold text-amber-600">{fmtMoney(totals?.incomePending ?? 0)}</p>
          <p className="text-[11px] text-amber-500 mt-0.5">待收款</p>
        </div>
        {/* 轧差只在真发生过结算时才有内容：0 元长期挂着一格会读成「抵消过但抵消完是 0」。
            这一格是历史累计，不参与上面两格加减——结算时那批单的已付金额已被抬高。 */}
        {totals && totals.nettingOffset > 0 && (
          <div
            className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-3 text-center border border-gray-200"
            title="已结算的轧差批次在本客户订单上抵消掉的累计金额；结算时已计入对应订单的已付款，故不再从待付款/待收款里扣减"
          >
            <p className="text-xl font-bold text-gray-600">{fmtMoney(totals.nettingOffset)}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">已轧差抵消</p>
          </div>
        )}
      </div>

      {/* Tab 切换 + 分享/导出 */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1 flex-1 bg-gray-100 rounded-lg p-1">
          {(['expense', 'income'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 text-xs font-medium rounded-md transition-all cursor-pointer ${
                activeTab === tab
                  ? `bg-white shadow-sm ${tab === 'expense' ? 'text-rose-600' : 'text-emerald-600'}`
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${tab === 'expense' ? 'ri-arrow-up-circle-line' : 'ri-arrow-down-circle-line'} mr-1`} />
              {tab === 'expense' ? '支出' : '收入'}订单({totals ? (tab === 'expense' ? totals.expenseOrders : totals.incomeOrders) : 0})
            </button>
          ))}
        </div>
        {/* 分享核对单：收件人是没注册账号的客户，拿到的是一条公开链接 */}
        {hasAnyOrders && (
          <button
            onClick={handleShare}
            title="生成订单核对链接，发给对方对账"
            className="flex items-center gap-1 px-2.5 py-2 text-xs rounded-lg border bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-share-line text-xs" />
            <span>分享核对</span>
          </button>
        )}
        {hasAnyOrders && (
          <div className="relative">
            <button
              onClick={() => setExportMenuOpen(v => !v)}
              disabled={exporting}
              title="导出该客户订单"
              className="flex items-center gap-1 px-2.5 py-2 text-xs rounded-lg border bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 transition-all cursor-pointer disabled:opacity-50"
            >
              {exporting
                ? <i className="ri-loader-4-line animate-spin text-xs" />
                : <i className="ri-download-2-line text-xs" />}
              <span>导出</span>
              <i className="ri-arrow-down-s-line text-xs text-gray-400" />
            </button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                <button
                  onClick={() => handleExport('xlsx')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-file-excel-line text-teal-500" />
                  Excel（全部）
                </button>
                <button
                  onClick={() => handleExport('xlsx', true)}
                  title="只导未付款的订单，部分付款与没有金额的历史单不在内"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-file-excel-line text-teal-500" />
                  Excel（未付款）
                </button>
                <div className="my-1 mx-2 border-t border-gray-100" />
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-file-pdf-line text-rose-500" />
                  PDF
                </button>
                <button
                  onClick={() => handleExport('print')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-printer-line text-gray-400" />
                  打印
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 说明这一节到底是哪些单：tab 上的数字是该方向全部有效单，列表只给最近的未执行单 */}
      <p className="text-[11px] text-gray-400 mb-2">
        仅列最近的 {CUSTOMER_ORDERS_PREVIEW_MAX} 条未执行订单，已完成 / 已取消的账目请到订单页按该客户查看
      </p>

      {current.isError ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p className="text-xs text-gray-500">订单列表读取失败</p>
          <button
            onClick={() => current.refetch()}
            className="px-3 py-1.5 text-xs rounded-lg border border-teal-200 text-teal-600 hover:bg-teal-50 cursor-pointer transition-colors"
          >
            重试
          </button>
        </div>
      ) : current.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-50 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center py-8 text-center">
          <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mb-2">
            <i className="ri-file-list-3-line text-gray-300 text-lg" />
          </div>
          <p className="text-xs text-gray-400">
            暂无未执行的{activeTab === 'expense' ? '支出' : '收入'}订单
          </p>
          {(directionCount ?? 0) > 0 && (
            <button onClick={goAllOrders} className="mt-2 text-xs text-teal-500 hover:text-teal-600 cursor-pointer">
              查看该客户全部订单
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {orders.map(order => (
              <OrderRow key={order.id} order={order} isExpense={activeTab === 'expense'} onOpen={() => navigate(`/orders?open=${order.id}`)} />
            ))}
          </div>
          <button
            onClick={goAllOrders}
            className="w-full mt-3 py-2 text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors flex items-center justify-center gap-1 border border-teal-100 rounded-xl hover:bg-teal-50"
          >
            <i className="ri-arrow-right-line text-sm" />
            查看全部订单
            {(directionCount ?? 0) > orders.length && `（共 ${directionCount} 单，已列 ${orders.length} 条）`}
          </button>
        </>
      )}

      {shareOpen && (
        <ShareCustomerModal
          customerId={customerId}
          customerName={customerName}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function OrderRow({ order, isExpense, onOpen }: { order: OrderItem; isExpense: boolean; onOpen: () => void }) {
  const balance = pendingBalanceOf(Number(order.orderAmount ?? 0), Number(order.paidAmount ?? 0));
  const days = calendarDaysUntil(order.orderStartDate);
  const due = dueLabel(days);
  // 待审核 / 已驳回要显式标出来：档案页看的是自己名下的在途单，含未过审的那批
  //（导出与核对单只收已通过的单，两边口径本来就不同）。已通过是常态，不占一格。
  const rejected = order.reviewStatus === '已拒绝';
  return (
    <div
      onClick={onOpen}
      className="flex items-center gap-3 rounded-xl p-3 bg-gray-50 hover:bg-teal-50/50 cursor-pointer transition-colors group"
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${
        rejected || days === null ? 'bg-gray-300' : days < 0 ? 'bg-rose-400' : 'bg-amber-400'
      }`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-mono text-gray-400 truncate">
            {order.orderNumber ?? order.id.slice(0, 8).toUpperCase()}
          </span>
          {order.serviceType?.name && (
            <span className="text-[10px] text-gray-400 bg-white border border-gray-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              {order.serviceType.name}
            </span>
          )}
          {order.reviewStatus === '待审核' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-50 text-amber-600 whitespace-nowrap">
              待审核
            </span>
          )}
          {rejected && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-rose-50 text-rose-500 whitespace-nowrap">
              已驳回
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{fmtDate(order.orderStartDate)}</span>
          {due && !rejected && <span className={`text-[10px] ${due.cls}`}>{due.text}</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        {order.orderAmount != null ? (
          <>
            <p className={`text-sm font-bold ${rejected ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
              {fmtMoney(Number(order.orderAmount))}
            </p>
            {!rejected && (
              <p className={`text-[10px] ${balance > 0 ? 'text-amber-500' : 'text-teal-500'}`}>
                {balance > 0 ? `${isExpense ? '待付' : '待收'} ${fmtMoney(balance)}` : '已结清'}
              </p>
            )}
          </>
        ) : (
          <span className="text-xs text-gray-300">-</span>
        )}
      </div>
      <div className="w-4 h-4 flex items-center justify-center text-gray-200 group-hover:text-teal-400 transition-colors shrink-0">
        <i className="ri-arrow-right-s-line text-sm" />
      </div>
    </div>
  );
}
